<template>
  <div :class="[menuClass]">
    <Notifications
      v-slot="{
        list,
        unread,
        markAsRead,
        markAsUnread,
        delete: deleteNotification,
      }"
    >
      <Menu
        v-slot="{ close }"
        as="div"
        class="antialiased relative mr-[14px] inline-block text-left ds:mr-0 ds:flex"
      >
        <MenuButton
          id="notif-menu-btn"
          aria-label="button"
          :class="['relative w-7 rounded-control py-[5px]', menuBtnClass]"
        >
          <FontAwesomeIcon
            :icon="['fad', 'bell']"
            class="text-title text-primary ds:text-[18px] ds:text-current"
          />

          <div
            v-if="unread.has"
            class="absolute -top-px right-[-3px] flex h-[15px] w-[15px] items-center justify-center rounded-full bg-[red] text-micro font-semibold leading-none text-white ds:-top-0.5 ds:-right-0.5 ds:h-[18px] ds:w-auto ds:min-w-[18px] ds:px-1 ds:bg-(--ds-status-error-solid) ds:text-[10px] ds:font-bold ds:ring-2 ds:ring-(--ds-surface-page)"
            v-text="unread.count"
          />
        </MenuButton>

        <transition
          enter-active-class="transition ease-out duration-100"
          enter-from-class="transform opacity-0 scale-95"
          enter-to-class="transform opacity-100 scale-100"
          leave-active-class="transition ease-in duration-75"
          leave-from-class="transform opacity-100 scale-100"
          leave-to-class="transform opacity-0 scale-95"
        >
          <MenuItems
            :class="[
              'absolute -right-6 z-50 w-96 origin-top-right overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 ds:w-[380px] ds:bg-(--ds-surface-default)',
              dropdownClass,
            ]"
          >
            <!-- Design-system look (ds:): the design system's overlay panel - a quiet header with
                 the count of unread, rows on hairlines with a brand dot for unread instead of the
                 4px bar, muted times, ghost icon buttons. -->
            <div
              class="flex items-center justify-between p-5 ds:border-b ds:border-(--ds-border-subtle) ds:px-4 ds:py-3"
            >
              <p
                role="heading"
                aria-level="2"
                class="font-semibold text-xl ds:flex ds:items-center ds:gap-2 ds:text-[16px] ds:text-(--ds-text-primary)"
              >
                {{ $t('notification_center.title') }}
                <span
                  v-if="unread.has"
                  class="hidden rounded-(--ds-radius-pill) bg-(--ds-surface-brand) px-2 py-0.5 text-[12px] font-semibold text-(--ds-text-brand) ds:inline-block"
                  data-test="notification-center-unread"
                  v-text="unread.count"
                />
              </p>
              <button
                type="button"
                :aria-label="$t('common.close')"
                class="ds:flex ds:size-8 ds:items-center ds:justify-center ds:rounded-(--ds-radius-sm) ds:text-(--ds-text-secondary) ds:transition-colors ds:hover:bg-(--ds-color-grey-50) ds:hover:text-(--ds-text-primary)"
                @click="close"
              >
                <FontAwesomeIcon
                  :icon="['fal', 'xmark']"
                  class="text-black text-xl ds:text-[18px] ds:text-current"
                />
              </button>
            </div>

            <p
              v-if="!list.length"
              class="py-3 px-2 text-center text-sm italic text-gray-900 ds:px-4 ds:py-8 ds:text-[14px] ds:not-italic ds:text-(--ds-text-secondary)"
              v-text="$t('common.no_notifications')"
            />
            <div
              class="max-h-[380px] overflow-y-auto ds:max-h-[min(420px,60dvh)]"
            >
              <MenuItem
                v-for="{
                  id,
                  title,
                  description,
                  read,
                  link,
                  timestamp,
                } in list"
                :key="id"
                disabled
              >
                <div
                  :class="[
                    'relative block border-b px-5 py-4 text-sm last:border-b-0 ds:border-(--ds-border-subtle) ds:py-3 ds:pr-4 ds:pl-7',
                    !read
                      ? 'border-[rgba(0,0,0,0.1)] ds:bg-(--ds-surface-default)'
                      : 'bg-[#F9F9F9] border-[rgba(0,0,0,0.2)] ds:bg-(--ds-surface-default)',
                  ]"
                >
                  <div
                    v-if="!read"
                    class="absolute top-0 left-0 h-full w-[4px] bg-primary ds:top-[18px] ds:left-3 ds:size-2 ds:rounded-full ds:bg-(--ds-action-brand)"
                    data-test="notification-unread-marker"
                  ></div>
                  <div class="flex gap-2">
                    <div class="flex-1 space-y-[9px]">
                      <p
                        class="text-[0.9rem] leading-none ds:text-[14px] ds:leading-snug"
                        :class="
                          !read
                            ? 'text-black font-bold ds:font-semibold ds:text-(--ds-text-primary)'
                            : 'font-medium ds:text-(--ds-text-secondary)'
                        "
                      >
                        <NotificationTitle :title="title" :link="link" />
                      </p>
                      <p
                        v-if="description"
                        class="text-[0.8rem] ds:text-[13px] ds:text-(--ds-text-secondary)"
                        v-text="description"
                      />
                      <div v-if="link && !useHasInlineLink({ title, link })">
                        <a
                          class="text-primary font-bold text-[0.8rem] inline-block ds:font-medium ds:text-[13px] ds:text-(--ds-text-brand) ds:hover:text-(--ds-text-brand-hover)"
                          :target="link.target ?? '_blank'"
                          :href="link.href"
                        >
                          {{ link.text }}
                        </a>
                      </div>
                    </div>

                    <div
                      v-if="timestamp"
                      class="space-y-1 text-[0.75rem] text-right leading-none ds:text-[12px] ds:text-(--ds-text-muted)"
                      :class="
                        !read
                          ? 'text-black font-semibold ds:font-medium ds:text-(--ds-text-secondary)'
                          : 'font-medium ds:font-normal'
                      "
                    >
                      <p v-text="formatDate(timestamp)"></p>
                      <p v-text="formatTime(timestamp)"></p>
                    </div>
                  </div>
                  <div
                    class="flex justify-end gap-x-4 text-xs font-medium ds:gap-x-0.5"
                    :class="
                      link && !useHasInlineLink({ title, link })
                        ? 'mt-4 ds:mt-1'
                        : 'mt-6 ds:mt-1'
                    "
                  >
                    <button
                      type="button"
                      aria-label="Mark as read/unread"
                      class="hover:opacity-70 transition duration-300 ease-in-out ds:flex ds:size-7 ds:items-center ds:justify-center ds:rounded-(--ds-radius-sm) ds:text-(--ds-text-muted) ds:hover:bg-(--ds-color-grey-50) ds:hover:text-(--ds-text-primary) ds:hover:opacity-100"
                      @click="
                        read ? markAsUnread.one({ id }) : markAsRead.one({ id })
                      "
                    >
                      <FontAwesomeIcon
                        :icon="
                          !read ? ['far', 'envelope-open'] : ['fal', 'envelope']
                        "
                        class="text-lg text-black ds:text-[15px] ds:text-current"
                        :class="read && 'opacity-50 ds:opacity-100'"
                      />
                    </button>

                    <button
                      type="button"
                      aria-label="Delete notification"
                      class="hover:opacity-70 transition duration-300 ease-in-out ds:flex ds:size-7 ds:items-center ds:justify-center ds:rounded-(--ds-radius-sm) ds:text-(--ds-text-muted) ds:hover:bg-(--ds-color-grey-50) ds:hover:text-(--ds-text-primary) ds:hover:opacity-100 ds:hover:text-(--ds-text-error)"
                      @click="deleteNotification.one({ id })"
                    >
                      <FontAwesomeIcon
                        :icon="['fal', 'trash-can']"
                        class="text-lg text-primary ds:text-[15px] ds:text-current"
                      />
                    </button>
                  </div>
                </div>
              </MenuItem>
            </div>

            <div
              v-if="list.length"
              class="flex items-center justify-end pt-5 pb-4 px-5 gap-x-6 ds:border-t ds:border-(--ds-border-subtle) ds:px-4 ds:py-2.5 ds:gap-x-1"
            >
              <button
                type="button"
                aria-label="Delete all notification"
                class="text-primary flex items-center font-medium gap-x-2 text-xs hover:opacity-80 transition duration-300 ease-in-out ds:h-8 ds:rounded-(--ds-radius-sm) ds:px-2.5 ds:text-[13px] ds:text-(--ds-text-brand) ds:hover:bg-(--ds-color-grey-50) ds:hover:opacity-100"
                @click="() => deleteNotification.all()"
              >
                <FontAwesomeIcon
                  :icon="['fal', 'trash-can']"
                  class="text-lg ds:text-[14px]"
                />

                {{ $t('notification_center.delete_all') }}
              </button>

              <button
                type="button"
                aria-label="Mark all as read"
                class="text-primary flex items-center font-medium gap-x-2 text-xs hover:opacity-80 transition duration-300 ease-in-out ds:h-8 ds:rounded-(--ds-radius-sm) ds:px-2.5 ds:text-[13px] ds:text-(--ds-text-brand) ds:hover:bg-(--ds-color-grey-50) ds:hover:opacity-100"
                @click="markAsRead.all()"
              >
                <FontAwesomeIcon
                  :icon="['fal', 'check-double']"
                  class="text-lg ds:text-[14px]"
                />

                {{ $t('notification_center.mark_all_as_read') }}
              </button>
            </div>
          </MenuItems>
        </transition>
      </Menu>
    </Notifications>
  </div>
</template>

<script setup>
import Notifications from '@/Components/Headless/Notifications'
import NotificationTitle from '@/Components/Notifications/NotificationTitle.vue'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { formatDate, formatTime } from '@/composables/date'
import { useHasInlineLink } from '@/composables/notification'

defineProps({
  menuClass: {
    type: String,
    default: '',
  },
  menuBtnClass: {
    type: String,
    default: '',
  },
  dropdownClass: {
    type: String,
    default: '',
  },
})
</script>
